import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../service/API';
import { listCompanyDomains } from '../CompanyDomain/companyDomainApi';
import { getApiErrorMessage } from '../../service/errorHandler';

const AddUserPage = () => {
  const apiClient = useApiClient();
  const [localPart, setLocalPart] = useState('');
  const [domain, setDomain] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const emailPreview = useMemo(() => {
    const username = localPart.trim();
    if (!username || !domain) return '';
    return `${username}@${domain}`.toLowerCase();
  }, [localPart, domain]);

  const loadRoles = async () => {
    try {
      const response = await apiClient.get('/roles');
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load roles.'));
    }
  };

  const loadDomains = async () => {
    setLoadingDomains(true);
    try {
      const data = await listCompanyDomains(apiClient);
      const activeDomains = (data || [])
        .filter((item) => item?.status === 'ACTIVE' && !item?.retiredAt)
        .sort((a, b) => (a.company || '').localeCompare(b.company || ''));

      setDomains(activeDomains);
      setDomain((current) => current || activeDomains[0]?.name || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load company domains.'));
    } finally {
      setLoadingDomains(false);
    }
  };

  useEffect(() => {
    loadRoles();
    loadDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRole = (event) => {
    const { value, checked } = event.target;
    setSelectedRoles((current) =>
      checked ? [...new Set([...current, value])] : current.filter((role) => role !== value),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');

    const username = localPart.trim();
    if (!username || username.includes('@')) {
      setError("Enter only the username portion before '@'.");
      return;
    }
    if (!domain) {
      setError('Select a company domain.');
      return;
    }
    if (selectedRoles.length === 0) {
      setError('Select at least one role.');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/users/add-user', {
        email: `${username}@${domain}`.toLowerCase(),
        roles: selectedRoles,
      });
      setMessage('User created successfully.');
      setLocalPart('');
      setSelectedRoles([]);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to create user.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <div>
          <h1 className="mb-1">Add User</h1>
          <p className="text-muted mb-0">Create a user and assign initial roles.</p>
        </div>
        <Link className="btn btn-outline-secondary" to="/manage-user">Back</Link>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label" htmlFor="username">Username</label>
                <input
                  id="username"
                  className="form-control"
                  value={localPart}
                  onChange={(event) => setLocalPart(event.target.value)}
                  placeholder="john.tan"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="col-12 col-md-6">
                <div className="d-flex justify-content-between align-items-center">
                  <label className="form-label" htmlFor="domain">Company Domain</label>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={loadDomains}
                    disabled={loadingDomains}
                  >
                    Refresh
                  </button>
                </div>
                <select
                  id="domain"
                  className="form-select"
                  value={domain}
                  onChange={(event) => setDomain(event.target.value)}
                  disabled={loadingDomains || domains.length === 0}
                  required
                >
                  {domains.length === 0 ? (
                    <option value="">No active domains</option>
                  ) : domains.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.company} ({item.name})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label" htmlFor="emailPreview">Email Preview</label>
                <input id="emailPreview" className="form-control" value={emailPreview} readOnly />
              </div>

              <div className="col-12">
                <fieldset>
                  <legend className="h6">Roles</legend>
                  <div className="row">
                    {roles.map((role) => (
                      <div className="col-12 col-sm-6 col-lg-4" key={role.id || role.name}>
                        <div className="form-check mb-2">
                          <input
                            id={`add-role-${role.id || role.name}`}
                            className="form-check-input"
                            type="checkbox"
                            value={role.name}
                            checked={selectedRoles.includes(role.name)}
                            onChange={toggleRole}
                          />
                          <label className="form-check-label" htmlFor={`add-role-${role.id || role.name}`}>
                            {role.name}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
            </div>

            <button className="btn btn-success mt-3" type="submit" disabled={submitting || !emailPreview}>
              {submitting ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddUserPage;